












































































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import { FETCH_ORG_PRICES, FETCH_ORG_PRODUCTS, SEGMENT_SCHEDULE_SAVE } from '@/store/action-types';
import { REMOVE_SEGMENT_FILTER, SET_SEGMENT_FILTER, STORE_SEGMENT_DATA } from '@/store/mutation-types';
import { ICustomAttribute, ISegment, ISegmentFilter, IState, SegmentAttribute, SegmentOperand, IABTest } from '@/store/types';
import VerticalLine from '@/components/UI/VerticalLine.vue';
import SegmentFilterEditor from '@/components/Builder/SegmentFilterEditor.vue';
import CustomAttributeEditor from '@/components/UI/CustomAttributeEditor.vue';
import {
  CUSTOM_ATTRIBUTE_TYPE,
  ENABLED_SEGMENT_ATTRIBUTES,
  getDefaultFilterValue,
  SAVE_STATE,
  segmentAttributeLabels,
  SEGMENT_ATTRIBUTE,
  SEGMENT_OPERAND,
  SEGMENT_ATTRIBUTE_TYPE,
} from '@/store/shared';

interface IAttributeOption {
  label: string;
  value: string;
  custom?: boolean;
}

const attributeOptions = ENABLED_SEGMENT_ATTRIBUTES.CANCEL_FLOW as string[];

export default Vue.extend({
  name: 'SegmentBuilder',
  components: {
    VerticalLine,
    SegmentFilterEditor,
    CustomAttributeEditor,
  },
  data() {
    return {
      loading: true,
      showAddCustom: false,
      segmentAttributeLabels,
      attributeOptions,
    };
  },
  async created() {
    await this.$store.dispatch(FETCH_ORG_PRICES);
    await this.$store.dispatch(FETCH_ORG_PRODUCTS);
    this.loading = false;
  },
  computed: {
    abTests(): IABTest[] {
      return this.$store.getters.abTests;
    },
    segment(): ISegment | null {
      return this.$store.getters.currentSegment;
    },
    segmentInABTest(): boolean {
      if (this.segment && this.abTests) {
        const segmentId = this.segment._id;
        return !!this.abTests.find((item: IABTest) => item.segments.includes(segmentId) && !item.completedAt);
      }
      return false;
    },
    segmentFilters(): ISegmentFilter[] {
      if (this.segment && this.segment.filter) {
        return this.segment.filter;
      }
      return [];
    },
    customAttributes(): ICustomAttribute[] {
      const state = this.$store.state as IState;
      return state.org?.customAttributes || [];
    },
    combinedAttributeOptions(): IAttributeOption[] {
      // combine attribute options and customAttributes
      return [
        ...this.attributeOptions.map((attribute) => ({
          label: segmentAttributeLabels[attribute as SegmentAttribute],
          value: attribute,
        })),
        ...this.customAttributes.map((attribute) => ({
          label: attribute.label,
          value: attribute.name,
          custom: true,
        })),
      ];
    },
  },
  methods: {
    // schedule a batch DB update of the segment
    scheduleSave() {
      if (this.segment) {
        this.$store.dispatch(SEGMENT_SCHEDULE_SAVE, { id: this.segment._id });
      }
    },
    // if it is published, mark unsaved to let users know that there are unsaved changes
    // if not published, autosave segment changes
    handleSaveBehavior() {
      if (this.segment) {
        if (!this.segment.activeBlueprint) {
          this.scheduleSave();
        } else {
          this.$store.commit(STORE_SEGMENT_DATA, { id: this.segment._id, data: { saveState: SAVE_STATE.UNSAVED } });
        }
      }
    },
    updateSegment(update: any) {
      if (this.segment) {
        this.$store.commit(STORE_SEGMENT_DATA, { id: this.segment._id, data: { saveState: SAVE_STATE.UNSAVED, ...update } });
        this.handleSaveBehavior();
      }
    },
    addFilter(attributeKey?: string) {
      let newFilter = {} as ISegmentFilter;
      // for both custom and default attributes
      if (attributeKey) {
        if (this.attributeOptions.includes(attributeKey)) {
          // default attribute
          const attributeType = SEGMENT_ATTRIBUTE_TYPE[attributeKey as SegmentAttribute];
          const operand = [
            SEGMENT_ATTRIBUTE.SUBSCRIPTION_START_DATE,
            SEGMENT_ATTRIBUTE.PRICE,
            SEGMENT_ATTRIBUTE.SUBSCRIPTION_AGE_MONTHS,
            SEGMENT_ATTRIBUTE.BILLING_INTERVAL_COUNT,
            SEGMENT_ATTRIBUTE.INVOICE_AMOUNT_DUE,
          ].includes(attributeKey as SegmentAttribute)
            ? SEGMENT_OPERAND.GTE
            : SEGMENT_OPERAND.INCLUDES;
          const newFilterValue = getDefaultFilterValue(attributeType, operand);
          newFilter = {
            attribute: attributeKey as SegmentAttribute,
            operand,
            value: newFilterValue,
          };
        } else {
          // custom attribute
          const match = this.customAttributes.find((attribute) => attribute.name === attributeKey);
          if (match) {
            let operand: SegmentOperand;
            if (match.type === CUSTOM_ATTRIBUTE_TYPE.NUMBER || match.type === CUSTOM_ATTRIBUTE_TYPE.DATE) {
              operand = SEGMENT_OPERAND.GTE;
            } else {
              operand = SEGMENT_OPERAND.INCLUDES;
            }
            const newFilterValue = getDefaultFilterValue(match.type, operand);
            newFilter = {
              attribute: attributeKey as SegmentAttribute,
              operand,
              value: newFilterValue,
              type: match.type,
            };
          }
        }
      }
      const newFilters = [...this.segmentFilters, newFilter];
      this.updateSegment({ filter: newFilters });
    },
    removeFilter(index: number) {
      if (this.segment) {
        this.$store.commit(REMOVE_SEGMENT_FILTER, { segmentId: this.segment._id, index });
        this.handleSaveBehavior();
      }
    },
    // pass entire new filter item
    updateFilterItem(index: number, data: ISegmentFilter) {
      if (this.segment) {
        this.$store.commit(SET_SEGMENT_FILTER, { segmentId: this.segment._id, index, data });
        this.handleSaveBehavior();
      }
    },
    nextStep() {
      this.$emit('next');
    },
  },
});
